// 用户行为跟踪实现

import type {
  ReadingPreferences,
  ReadingGoals
} from '@/types/reading';

import type { StorageManager } from './storage';

export interface UserBehaviorEvent {
  id: string;
  type: BehaviorEventType;
  timestamp: Date;
  bookId?: string;
  chapterId?: string;
  data?: any;
  sessionId: string;
}

export type BehaviorEventType = 
  | 'book_opened'
  | 'chapter_read'
  | 'book_finished'
  | 'book_favorited'
  | 'book_rated'
  | 'search_performed'
  | 'category_browsed'
  | 'bookmark_added'
  | 'note_added'
  | 'reading_session_started'
  | 'reading_session_ended'
  | 'settings_changed';

export interface ReadingSession {
  id: string;
  bookId: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // milliseconds
  chaptersRead: string[];
  wordsRead: number;
  averageReadingSpeed: number; // words per minute
}

export interface UserPreferenceProfile {
  favoriteCategories: Array<{ category: string; score: number }>;
  favoriteAuthors: Array<{ author: string; score: number }>;
  favoriteGenres: Array<{ genre: string; score: number }>;
  readingPatterns: {
    preferredReadingTime: string[]; // hours of day
    averageSessionDuration: number; // minutes
    booksPerMonth: number;
    chaptersPerSession: number;
  };
  contentPreferences: {
    preferredBookLength: 'short' | 'medium' | 'long';
    preferredUpdateFrequency: 'completed' | 'ongoing';
    languagePreference: string[];
  };
}

export class UserBehaviorTracker {
  private storage: StorageManager;
  private currentSessionId: string;
  private behaviorEvents: UserBehaviorEvent[] = [];
  private readonly MAX_EVENTS_IN_MEMORY = 100;
  private readonly BATCH_SAVE_INTERVAL = 30000; // 30秒批量保存
  private saveTimer?: NodeJS.Timeout;

  constructor(storage: StorageManager) {
    this.storage = storage;
    this.currentSessionId = this.generateSessionId();
    this.startBatchSaving();
  }

  // ============================================================================
  // 阅读偏好记录系统
  // ============================================================================

  async trackEvent(
    type: BehaviorEventType,
    bookId?: string,
    chapterId?: string,
    data?: any
  ): Promise<void> {
    try {
      const event: UserBehaviorEvent = {
        id: this.generateEventId(),
        type,
        timestamp: new Date(),
        bookId,
        chapterId,
        data,
        sessionId: this.currentSessionId
      };

      this.behaviorEvents.push(event);

      // 如果内存中事件过多，立即保存
      if (this.behaviorEvents.length >= this.MAX_EVENTS_IN_MEMORY) {
        await this.saveBehaviorEvents();
      }

      // 处理特定事件类型
      await this.processSpecificEvent(event);

    } catch (error) {
      console.error('Track event failed:', error);
    }
  }

  private async processSpecificEvent(event: UserBehaviorEvent): Promise<void> {
    switch (event.type) {
      case 'book_opened':
        await this.updateBookOpenCount(event.bookId!);
        break;
      
      case 'chapter_read':
        await this.updateReadingProgress(event.bookId!, event.chapterId!);
        break;
      
      case 'book_finished':
        await this.recordBookCompletion(event.bookId!);
        break;
      
      case 'book_favorited':
        await this.updateFavoritePreferences(event.bookId!);
        break;
      
      case 'book_rated':
        await this.updateRatingPreferences(event.bookId!, event.data?.rating);
        break;
      
      case 'search_performed':
        await this.updateSearchPreferences(event.data?.keyword, event.data?.category);
        break;
      
      case 'category_browsed':
        await this.updateCategoryPreferences(event.data?.category);
        break;
    }
  }

  private async updateBookOpenCount(bookId: string): Promise<void> {
    try {
      const openCounts = await this.storage.getUserData('book_open_counts') || {};
      openCounts[bookId] = (openCounts[bookId] || 0) + 1;
      await this.storage.saveUserData('book_open_counts', openCounts);
    } catch (error) {
      console.error('Update book open count failed:', error);
    }
  }

  private async updateReadingProgress(bookId: string, chapterId: string): Promise<void> {
    try {
      const readingHistory = await this.storage.getUserData('reading_history') || [];
      const today = new Date().toDateString();
      
      let todayEntry = readingHistory.find((entry: any) => 
        entry.date === today && entry.bookId === bookId
      );

      if (!todayEntry) {
        todayEntry = {
          date: today,
          bookId,
          chaptersRead: [],
          readingTime: 0
        };
        readingHistory.push(todayEntry);
      }

      if (!todayEntry.chaptersRead.includes(chapterId)) {
        todayEntry.chaptersRead.push(chapterId);
      }

      await this.storage.saveUserData('reading_history', readingHistory);
    } catch (error) {
      console.error('Update reading progress failed:', error);
    }
  }

  private async recordBookCompletion(bookId: string): Promise<void> {
    try {
      const completedBooks = await this.storage.getUserData('completed_books') || [];
      
      if (!completedBooks.includes(bookId)) {
        completedBooks.push(bookId);
        await this.storage.saveUserData('completed_books', completedBooks);
      }
    } catch (error) {
      console.error('Record book completion failed:', error);
    }
  }

  private async updateFavoritePreferences(_bookId: string): Promise<void> {
    try {
      // 这里需要获取图书信息来更新偏好
      // 暂时跳过实现
    } catch (error) {
      console.error('Update favorite preferences failed:', error);
    }
  }

  private async updateRatingPreferences(bookId: string, rating: number): Promise<void> {
    try {
      const ratingHistory = await this.storage.getUserData('rating_history') || [];
      
      ratingHistory.push({
        bookId,
        rating,
        timestamp: new Date()
      });

      await this.storage.saveUserData('rating_history', ratingHistory);
    } catch (error) {
      console.error('Update rating preferences failed:', error);
    }
  }

  private async updateSearchPreferences(keyword?: string, category?: string): Promise<void> {
    try {
      const searchPrefs = await this.storage.getUserData('search_preferences') || {
        keywords: {},
        categories: {}
      };

      if (keyword) {
        searchPrefs.keywords[keyword] = (searchPrefs.keywords[keyword] || 0) + 1;
      }

      if (category) {
        searchPrefs.categories[category] = (searchPrefs.categories[category] || 0) + 1;
      }

      await this.storage.saveUserData('search_preferences', searchPrefs);
    } catch (error) {
      console.error('Update search preferences failed:', error);
    }
  }

  private async updateCategoryPreferences(category: string): Promise<void> {
    try {
      const categoryPrefs = await this.storage.getUserData('category_preferences') || {};
      categoryPrefs[category] = (categoryPrefs[category] || 0) + 1;
      await this.storage.saveUserData('category_preferences', categoryPrefs);
    } catch (error) {
      console.error('Update category preferences failed:', error);
    }
  }

  // ============================================================================
  // 用户行为分析
  // ============================================================================

  async analyzeUserBehavior(): Promise<UserPreferenceProfile> {
    try {
      const [
        categoryPrefs,
        _searchPrefs,
        _ratingHistory,
        readingHistory,
        _completedBooks
      ] = await Promise.all([
        this.storage.getUserData('category_preferences') || {},
        this.storage.getUserData('search_preferences') || { keywords: {}, categories: {} },
        this.storage.getUserData('rating_history') || [],
        this.storage.getUserData('reading_history') || [],
        this.storage.getUserData('completed_books') || []
      ]);

      // 分析最喜欢的分类
      const favoriteCategories = Object.entries(categoryPrefs)
        .map(([category, count]) => ({ category, score: count as number }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);

      // 分析阅读模式
      const readingPatterns = this.analyzeReadingPatterns(readingHistory);

      // 分析内容偏好
      const contentPreferences = await this.analyzeContentPreferences(_ratingHistory, _completedBooks);

      return {
        favoriteCategories,
        favoriteAuthors: [], // 需要从图书信息中分析
        favoriteGenres: [], // 需要从图书标签中分析
        readingPatterns,
        contentPreferences
      };

    } catch (error) {
      console.error('Analyze user behavior failed:', error);
      return this.getDefaultPreferenceProfile();
    }
  }

  private analyzeReadingPatterns(readingHistory: any[]): UserPreferenceProfile['readingPatterns'] {
    if (readingHistory.length === 0) {
      return {
        preferredReadingTime: [],
        averageSessionDuration: 0,
        booksPerMonth: 0,
        chaptersPerSession: 0
      };
    }

    // 分析阅读时间偏好（这里需要更详细的时间戳数据）
    const preferredReadingTime: string[] = [];

    // 计算平均会话时长
    const totalReadingTime = readingHistory.reduce((sum, entry) => sum + (entry.readingTime || 0), 0);
    const averageSessionDuration = totalReadingTime / readingHistory.length;

    // 计算每月图书数量
    const monthlyBooks = new Set(readingHistory.map(entry => entry.bookId)).size;
    const monthsSpan = Math.max(1, readingHistory.length / 30); // 简化计算
    const booksPerMonth = monthlyBooks / monthsSpan;

    // 计算每次会话平均章节数
    const totalChapters = readingHistory.reduce((sum, entry) => sum + (entry.chaptersRead?.length || 0), 0);
    const chaptersPerSession = totalChapters / readingHistory.length;

    return {
      preferredReadingTime,
      averageSessionDuration,
      booksPerMonth,
      chaptersPerSession
    };
  }

  private async analyzeContentPreferences(
    _ratingHistory: any[],
    _completedBooks: string[]
  ): Promise<UserPreferenceProfile['contentPreferences']> {
    // 这里需要分析图书长度、更新频率等偏好
    // 暂时返回默认值
    return {
      preferredBookLength: 'medium',
      preferredUpdateFrequency: 'ongoing',
      languagePreference: ['zh-CN']
    };
  }

  private getDefaultPreferenceProfile(): UserPreferenceProfile {
    return {
      favoriteCategories: [],
      favoriteAuthors: [],
      favoriteGenres: [],
      readingPatterns: {
        preferredReadingTime: [],
        averageSessionDuration: 0,
        booksPerMonth: 0,
        chaptersPerSession: 0
      },
      contentPreferences: {
        preferredBookLength: 'medium',
        preferredUpdateFrequency: 'ongoing',
        languagePreference: ['zh-CN']
      }
    };
  }

  // ============================================================================
  // 偏好设置管理
  // ============================================================================

  async getReadingPreferences(): Promise<ReadingPreferences> {
    try {
      const preferences = await this.storage.getUserData('reading_preferences');
      
      if (preferences) {
        return preferences;
      }

      // 如果没有保存的偏好，从行为分析中生成
      const profile = await this.analyzeUserBehavior();
      
      const defaultPreferences: ReadingPreferences = {
        preferredCategories: profile.favoriteCategories.map(cat => cat.category),
        preferredAuthors: profile.favoriteAuthors.map(author => author.author),
        preferredTags: [],
        excludedCategories: [],
        readingGoals: {
          dailyReadingTime: 60, // 60分钟
          weeklyBooks: 1,
          monthlyBooks: 4
        }
      };

      await this.storage.saveUserData('reading_preferences', defaultPreferences);
      return defaultPreferences;

    } catch (error) {
      console.error('Get reading preferences failed:', error);
      return {
        preferredCategories: [],
        preferredAuthors: [],
        preferredTags: [],
        excludedCategories: [],
        readingGoals: {
          dailyReadingTime: 60,
          weeklyBooks: 1,
          monthlyBooks: 4
        }
      };
    }
  }

  async updateReadingPreferences(preferences: Partial<ReadingPreferences>): Promise<void> {
    try {
      const currentPreferences = await this.getReadingPreferences();
      const updatedPreferences = {
        ...currentPreferences,
        ...preferences
      };

      await this.storage.saveUserData('reading_preferences', updatedPreferences);
      
      // 记录偏好更新事件
      await this.trackEvent('settings_changed', undefined, undefined, { 
        type: 'preferences',
        changes: preferences 
      });

    } catch (error) {
      console.error('Update reading preferences failed:', error);
    }
  }

  async getReadingGoals(): Promise<ReadingGoals> {
    try {
      const preferences = await this.getReadingPreferences();
      return preferences.readingGoals;
    } catch (error) {
      console.error('Get reading goals failed:', error);
      return {
        dailyReadingTime: 60,
        weeklyBooks: 1,
        monthlyBooks: 4
      };
    }
  }

  async updateReadingGoals(goals: Partial<ReadingGoals>): Promise<void> {
    try {
      const preferences = await this.getReadingPreferences();
      preferences.readingGoals = {
        ...preferences.readingGoals,
        ...goals
      };

      await this.updateReadingPreferences(preferences);
    } catch (error) {
      console.error('Update reading goals failed:', error);
    }
  }

  // ============================================================================
  // 行为统计和报告
  // ============================================================================

  async getBehaviorStats(days: number = 30): Promise<{
    totalEvents: number;
    eventsByType: Record<BehaviorEventType, number>;
    dailyActivity: Array<{ date: string; events: number }>;
    mostActiveHours: number[];
    readingStreak: number;
  }> {
    try {
      const events = await this.getAllBehaviorEvents(days);
      
      // 按类型统计事件
      const eventsByType: Record<BehaviorEventType, number> = {} as any;
      events.forEach(event => {
        eventsByType[event.type] = (eventsByType[event.type] || 0) + 1;
      });

      // 按日期统计活动
      const dailyActivity = this.calculateDailyActivity(events, days);

      // 计算最活跃时间段
      const mostActiveHours = this.calculateMostActiveHours(events);

      // 计算阅读连续天数
      const readingStreak = this.calculateReadingStreak(events);

      return {
        totalEvents: events.length,
        eventsByType,
        dailyActivity,
        mostActiveHours,
        readingStreak
      };

    } catch (error) {
      console.error('Get behavior stats failed:', error);
      return {
        totalEvents: 0,
        eventsByType: {} as any,
        dailyActivity: [],
        mostActiveHours: [],
        readingStreak: 0
      };
    }
  }

  private async getAllBehaviorEvents(days: number): Promise<UserBehaviorEvent[]> {
    try {
      // 这里需要从存储中获取历史事件
      // 当前返回内存中的事件
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      return this.behaviorEvents.filter(event => event.timestamp >= cutoffDate);
    } catch (error) {
      console.error('Get all behavior events failed:', error);
      return [];
    }
  }

  private calculateDailyActivity(events: UserBehaviorEvent[], days: number): Array<{ date: string; events: number }> {
    const dailyMap = new Map<string, number>();
    
    // 初始化所有日期
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dailyMap.set(date.toDateString(), 0);
    }

    // 统计每日事件
    events.forEach(event => {
      const dateStr = event.timestamp.toDateString();
      if (dailyMap.has(dateStr)) {
        dailyMap.set(dateStr, dailyMap.get(dateStr)! + 1);
      }
    });

    return Array.from(dailyMap.entries())
      .map(([date, events]) => ({ date, events }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  private calculateMostActiveHours(events: UserBehaviorEvent[]): number[] {
    const hourCounts = new Array(24).fill(0);
    
    events.forEach(event => {
      const hour = event.timestamp.getHours();
      hourCounts[hour]++;
    });

    // 返回最活跃的3个小时
    return hourCounts
      .map((count, hour) => ({ hour, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map(item => item.hour);
  }

  private calculateReadingStreak(events: UserBehaviorEvent[]): number {
    const readingEvents = events.filter(event => 
      ['book_opened', 'chapter_read'].includes(event.type)
    );

    if (readingEvents.length === 0) {
      return 0;
    }

    // 按日期分组
    const dateSet = new Set(
      readingEvents.map(event => event.timestamp.toDateString())
    );

    const sortedDates = Array.from(dateSet).sort((a, b) => 
      new Date(b).getTime() - new Date(a).getTime()
    );

    // 计算连续天数
    let streak = 0;
    
    for (let i = 0; i < sortedDates.length; i++) {
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      
      if (sortedDates[i] === expectedDate.toDateString()) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  }

  // ============================================================================
  // 数据管理
  // ============================================================================

  private async saveBehaviorEvents(): Promise<void> {
    try {
      if (this.behaviorEvents.length === 0) {
        return;
      }

      // 获取现有事件
      const existingEvents = await this.storage.getUserData('behavior_events') || [];
      
      // 合并新事件
      const allEvents = [...existingEvents, ...this.behaviorEvents];
      
      // 保持最近的事件（限制数量）
      const maxEvents = 10000;
      const eventsToSave = allEvents
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, maxEvents);

      await this.storage.saveUserData('behavior_events', eventsToSave);
      
      // 清空内存中的事件
      this.behaviorEvents = [];

    } catch (error) {
      console.error('Save behavior events failed:', error);
    }
  }

  private startBatchSaving(): void {
    this.saveTimer = setInterval(async () => {
      await this.saveBehaviorEvents();
    }, this.BATCH_SAVE_INTERVAL);
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateEventId(): string {
    return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ============================================================================
  // 清理和销毁
  // ============================================================================

  async destroy(): Promise<void> {
    if (this.saveTimer) {
      clearInterval(this.saveTimer);
    }
    
    await this.saveBehaviorEvents();
  }
}